import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { db, storage } from "./config";

import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { resizeImage, FULL_MAX_EDGE, THUMB_MAX_EDGE } from "../utils/images";

// Collections
const REVIEWS_COLLECTION = "reviews";
const SUBSCRIBERS_COLLECTION = "subscribers";

// Review operations
export const addReview = async (reviewData) => {
  try {
    console.log("🔄 Adding review to Firestore...", reviewData);

    // Ensure we have valid data
    if (!reviewData.title || !reviewData.content || !reviewData.excerpt) {
      throw new Error("Missing required review fields");
    }

    // Use plain JavaScript Date instead of Firestore Timestamp
    const now = new Date().toISOString();

    // Create clean data object with only primitive types
    const docData = {
      title: String(reviewData.title).trim(),
      rating: Number(reviewData.rating),
      excerpt: String(reviewData.excerpt).trim(),
      content: String(reviewData.content).trim(),
      date: reviewData.date || new Date().toISOString().split("T")[0],
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, REVIEWS_COLLECTION), docData);

    console.log("✅ Review added with ID:", docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("❌ Error adding review:", error);
    console.error("Error code:", error.code);
    console.error("Error message:", error.message);
    console.error("Full error:", error);

    // Return more specific error message
    let errorMessage = error.message;
    if (error.code === "permission-denied") {
      errorMessage =
        "Permission denied. Make sure Firestore rules allow writes.";
    } else if (error.code === "invalid-argument") {
      errorMessage = "Invalid data format. Check browser console for details.";
    } else if (error.code === "unavailable") {
      errorMessage =
        "Firestore is unavailable. Check your internet connection.";
    } else if (error.code === "not-found") {
      errorMessage =
        "Firestore database not found. Create it in Firebase Console.";
    }

    return { success: false, error: errorMessage };
  }
};

export const updateReview = async (reviewId, reviewData) => {
  try {
    const reviewRef = doc(db, REVIEWS_COLLECTION, reviewId);

    const updateData = {
      title: String(reviewData.title || ""),
      rating: Number(reviewData.rating || 5),
      excerpt: String(reviewData.excerpt || ""),
      content: String(reviewData.content || ""),
      updatedAt: Timestamp.now(),
    };

    // Remove undefined fields
    Object.keys(updateData).forEach((key) => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });

    await updateDoc(reviewRef, updateData);
    return { success: true };
  } catch (error) {
    console.error("Error updating review:", error);
    return { success: false, error: error.message };
  }
};

export const deleteReview = async (reviewId) => {
  try {
    await deleteDoc(doc(db, REVIEWS_COLLECTION, reviewId));
    return { success: true };
  } catch (error) {
    console.error("Error deleting review:", error);
    return { success: false, error: error.message };
  }
};

export const getReviews = async () => {
  try {
    console.log("📖 Fetching reviews from Firestore...");

    // Simple query without orderBy to avoid index issues
    const reviewsRef = collection(db, REVIEWS_COLLECTION);
    const querySnapshot = await getDocs(reviewsRef);

    console.log("Found documents:", querySnapshot.size);

    const reviews = [];
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();

      reviews.push({
        id: docSnapshot.id,
        title: data.title || "",
        rating: data.rating || 5,
        excerpt: data.excerpt || "",
        content: data.content || "",
        date:
          data.date ||
          (data.createdAt
            ? data.createdAt.toDate().toISOString().split("T")[0]
            : new Date().toISOString().split("T")[0]),
      });
    });

    // Sort in JavaScript instead of Firestore
    reviews.sort((a, b) => new Date(b.date) - new Date(a.date));

    console.log("✅ Reviews loaded:", reviews.length);
    return { success: true, reviews, fromCache: querySnapshot.metadata.fromCache };
  } catch (error) {
    console.error("❌ Error getting reviews:", error);
    console.error("Error code:", error.code);
    console.error("Error message:", error.message);

    if (error.code === "failed-precondition") {
      return {
        success: false,
        error:
          "Database index required. Check console for index creation link.",
      };
    }

    return { success: false, error: error.message };
  }
};

export const getReview = async (reviewId) => {
  try {
    const docRef = doc(db, REVIEWS_COLLECTION, reviewId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        success: true,
        review: {
          id: docSnap.id,
          title: data.title || "",
          rating: data.rating || 5,
          excerpt: data.excerpt || "",
          content: data.content || "",
          date:
            data.date ||
            data.createdAt?.toDate().toISOString().split("T")[0] ||
            new Date().toISOString().split("T")[0],
        },
      };
    } else {
      return { success: false, error: "Review not found" };
    }
  } catch (error) {
    console.error("Error getting review:", error);
    return { success: false, error: error.message };
  }
};

// Subscriber operations
export const addSubscriber = async (email) => {
  try {
    // Check if subscriber already exists
    const q = query(
      collection(db, SUBSCRIBERS_COLLECTION),
      where("email", "==", email)
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      return { success: false, error: "Email already subscribed" };
    }

    const docData = {
      email: String(email),
      subscribedAt: Timestamp.now(),
      isActive: true,
    };

    const docRef = await addDoc(
      collection(db, SUBSCRIBERS_COLLECTION),
      docData
    );
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Error adding subscriber:", error);
    return { success: false, error: error.message };
  }
};

export const getSubscribers = async () => {
  try {
    console.log("📖 Fetching subscribers from Firestore...");

    // Simple query without orderBy to avoid index issues
    const subscribersRef = collection(db, SUBSCRIBERS_COLLECTION);
    const q = query(subscribersRef, where("isActive", "==", true));
    const querySnapshot = await getDocs(q);

    const subscribers = [];
    querySnapshot.forEach((docSnapshot) => {
      subscribers.push({
        id: docSnapshot.id,
        ...docSnapshot.data(),
      });
    });

    // Sort in JavaScript
    subscribers.sort((a, b) => {
      const dateA = a.subscribedAt?.toDate() || new Date(0);
      const dateB = b.subscribedAt?.toDate() || new Date(0);
      return dateB - dateA;
    });

    console.log("✅ Subscribers loaded:", subscribers.length);
    return { success: true, subscribers };
  } catch (error) {
    console.error("❌ Error getting subscribers:", error);
    return { success: false, error: error.message };
  }
};

export const unsubscribeEmail = async (email) => {
  try {
    const q = query(
      collection(db, SUBSCRIBERS_COLLECTION),
      where("email", "==", email)
    );
    const querySnapshot = await getDocs(q);

    const updatePromises = [];
    querySnapshot.forEach((docSnapshot) => {
      updatePromises.push(updateDoc(docSnapshot.ref, { isActive: false }));
    });

    await Promise.all(updatePromises);
    return { success: true };
  } catch (error) {
    console.error("Error unsubscribing:", error);
    return { success: false, error: error.message };
  }
};

// Projects operations
const PROJECTS_COLLECTION = "projects";

export const addProject = async (projectData) => {
  try {
    const docData = {
      title: String(projectData.title).trim(),
      description: String(projectData.description).trim(),
      tech: projectData.tech || [],
      githubUrl: String(projectData.githubUrl || "").trim(),
      liveUrl: String(projectData.liveUrl || "").trim(),
      createdAt: new Date().toISOString(),
    };

    const docRef = await addDoc(collection(db, PROJECTS_COLLECTION), docData);
    console.log("✅ Project added with ID:", docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("❌ Error adding project:", error);
    return { success: false, error: error.message };
  }
};

export const updateProject = async (projectId, projectData) => {
  try {
    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);

    const updateData = {
      title: String(projectData.title).trim(),
      description: String(projectData.description).trim(),
      tech: projectData.tech || [],
      githubUrl: String(projectData.githubUrl || "").trim(),
      liveUrl: String(projectData.liveUrl || "").trim(),
      updatedAt: new Date().toISOString(),
    };

    await updateDoc(projectRef, updateData);
    return { success: true };
  } catch (error) {
    console.error("Error updating project:", error);
    return { success: false, error: error.message };
  }
};

export const deleteProject = async (projectId) => {
  try {
    await deleteDoc(doc(db, PROJECTS_COLLECTION, projectId));
    return { success: true };
  } catch (error) {
    console.error("Error deleting project:", error);
    return { success: false, error: error.message };
  }
};

export const getProjects = async () => {
  try {
    console.log("📖 Fetching projects from Firestore...");

    const projectsRef = collection(db, PROJECTS_COLLECTION);
    const querySnapshot = await getDocs(projectsRef);

    const projects = [];
    querySnapshot.forEach((docSnapshot) => {
      projects.push({
        id: docSnapshot.id,
        ...docSnapshot.data(),
      });
    });

    // Sort by creation date
    projects.sort(
      (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    );

    console.log("✅ Projects loaded:", projects.length);
    return { success: true, projects, fromCache: querySnapshot.metadata.fromCache };
  } catch (error) {
    console.error("❌ Error getting projects:", error);
    return { success: false, error: error.message };
  }
};

const IMAGE_COLLECTIONS = "photos";

export const saveImageData = async (img) => {
  try {
    const { url, thumbUrl } = await uploadImage(img);
    const docData = {
      title: String(img.title || "").trim(),
      description: String(img.description || "").trim(),
      category: String(img.category || ""),
      url,
      thumbUrl,
    };

    const docRef = await addDoc(collection(db, IMAGE_COLLECTIONS), docData);
    console.log("✅ Image added with ID:", docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("❌ Error adding image:", error);
    return { success: false, error: error.message };
  }
};

// Uploads a downscaled display copy plus a small thumbnail. Names are unique
// so two photos called "IMG_0001.jpg" no longer overwrite each other, and both
// files sit directly under images/ where storage.rules allow writes.
const uploadImage = async (img) => {
  const base = `${Date.now()}-${img.file.name
    .replace(/\.[^.]+$/, "")
    .replace(/[^a-z0-9_-]+/gi, "-")
    .slice(0, 60)}`;

  const [full, thumb] = await Promise.all([
    resizeImage(img.file, FULL_MAX_EDGE),
    resizeImage(img.file, THUMB_MAX_EDGE, 0.8),
  ]);
  const fullRef = ref(storage, `images/${base}.jpg`);
  const thumbRef = ref(storage, `images/${base}-thumb.jpg`);
  const metadata = {
    contentType: full.type || "image/jpeg",
    cacheControl: "public, max-age=31536000, immutable",
  };

  await Promise.all([
    uploadBytes(fullRef, full, metadata),
    uploadBytes(thumbRef, thumb, { ...metadata, contentType: thumb.type || "image/jpeg" }),
  ]);
  const [url, thumbUrl] = await Promise.all([
    getDownloadURL(fullRef),
    getDownloadURL(thumbRef),
  ]);
  return { url, thumbUrl };
};

export const getImages = async () => {
  try {
    console.log("📖 Fetching images from Firestore...");

    const projectsRef = collection(db, IMAGE_COLLECTIONS);
    const querySnapshot = await getDocs(projectsRef);

    const images = [];
    querySnapshot.forEach((docSnapshot) => {
      images.push({
        id: docSnapshot.id,
        ...docSnapshot.data(),
      });
    });
    return { success: true, images, fromCache: querySnapshot.metadata.fromCache };
  } catch (error) {
    console.error("❌ Error getting images:", error);
    return { success: false, error: error.message };
  }
};

export const deleteImage = async (photoId, imageUrl, thumbUrl) => {
  try {
    for (const fileUrl of [imageUrl, thumbUrl]) {
      if (!fileUrl) continue;
      try {
        await deleteObject(ref(storage, fileUrl));
      } catch (storageError) {
        // Storage object may already be gone; still remove the Firestore record
        console.error("⚠️ Error deleting storage object:", storageError);
      }
    }

    await deleteDoc(doc(db, IMAGE_COLLECTIONS, photoId));
    console.log("✅ Image deleted:", photoId);
    return { success: true };
  } catch (error) {
    console.error("❌ Error deleting image:", error);
    return { success: false, error: error.message };
  }
};
